import API_CONFIG, { API_ENDPOINTS } from '../../config/api';
import { buildApiUrl } from '../../utils/apiUrl';

export class DocumentService {
  private async fetchWithAuth(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = localStorage.getItem('access_token');
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Ne pas ajouter Content-Type pour FormData (le navigateur le fait automatiquement)
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // BEST PRACTICE: Utiliser buildApiUrl() pour construire l'URL de manière sûre
    const url = buildApiUrl(endpoint);
    return fetch(url, { ...options, headers });
  }

  private async get<T>(endpoint: string): Promise<T> {
    const response = await this.fetchWithAuth(endpoint);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  private async post<T>(endpoint: string, data: any, isJson: boolean = true): Promise<T> {
    const options: RequestInit = {
      method: 'POST',
      body: isJson ? JSON.stringify(data) : data,
    };
    const response = await this.fetchWithAuth(endpoint, options);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  private async patch<T>(endpoint: string, data: any): Promise<T> {
    const options: RequestInit = {
      method: 'PATCH',
      body: JSON.stringify(data),
    };
    const response = await this.fetchWithAuth(endpoint, options);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  private async delete(endpoint: string): Promise<void> {
    const options: RequestInit = {
      method: 'DELETE',
    };
    const response = await this.fetchWithAuth(endpoint, options);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }
  async getDocuments(): Promise<any[]> {
    try {
      console.log('📄 Chargement des documents depuis l\'API...');
      const response = await this.get<any>(API_ENDPOINTS.DOCUMENTS);
      console.log('✅ Documents reçus:', response);
      return response.results || response || [];
    } catch (error) {
      console.error('❌ Erreur lors du chargement des documents:', error);
      throw error;
    }
  }

  async uploadDocument(documentData: any): Promise<any> {
    try {
      console.log('📤 Upload du document:', documentData);
      const formData = new FormData();
      
      // Fichier (obligatoire)
      if (documentData.file) {
        formData.append('file', documentData.file);
      }
      
      // Champs obligatoires
      formData.append('name', documentData.name || documentData.title || 'Document sans titre');
      formData.append('description', documentData.description || '');
      formData.append('is_public', documentData.isPublic ? 'true' : 'false');
      formData.append('cloud_provider', documentData.cloudProvider || 'local');
      
      // Type de document (document_type attend un ID)
      if (documentData.documentType) {
        formData.append('document_type', documentData.documentType);
      }
      
      // Sous-type de document (document_subtype attend un ID)
      if (documentData.documentSubtype) {
        formData.append('document_subtype', documentData.documentSubtype);
      }
      
      // Projet (optionnel, attend un ID)
      if (documentData.project) {
        formData.append('project', documentData.project);
      }
      
      // Tags (optionnel, format JSON array)
      if (documentData.tags && documentData.tags.length > 0) {
        const tagsArray = typeof documentData.tags === 'string' 
          ? documentData.tags.split(',').map((t: string) => t.trim()).filter((t: string) => t)
          : documentData.tags;
        if (tagsArray.length > 0) {
          formData.append('tags', JSON.stringify(tagsArray));
        }
      }
      
      console.log('📦 FormData préparé:', {
        name: documentData.name,
        description: documentData.description?.substring(0, 50) + '...',
        hasFile: !!documentData.file,
        fileName: documentData.file?.name,
        documentType: documentData.documentType,
        documentSubtype: documentData.documentSubtype,
        project: documentData.project,
        tags: documentData.tags,
        isPublic: documentData.isPublic
      });
      
      const response = await this.post<any>(API_ENDPOINTS.DOCUMENTS, formData, false);
      console.log('✅ Document uploadé:', response);
      return response;
    } catch (error) {
      console.error('❌ Erreur lors de l\'upload:', error);
      throw error;
    }
  }

  async deleteDocument(documentId: string): Promise<{ success: boolean }> {
    try {
      console.log('🗑️ Suppression du document:', documentId);
      await this.delete(`${API_ENDPOINTS.DOCUMENTS}${documentId}/`);
      console.log('✅ Document supprimé');
      return { success: true };
    } catch (error) {
      console.error('❌ Erreur lors de la suppression:', error);
      throw error;
    }
  }

  async updateDocument(documentId: string, documentData: any): Promise<any> {
    try {
      console.log('✏️ Mise à jour du document:', documentId, documentData);
      const response = await this.patch<any>(`${API_ENDPOINTS.DOCUMENTS}${documentId}/`, documentData);
      console.log('✅ Document mis à jour:', response);
      return response;
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour:', error);
      throw error;
    }
  }

  async getDocumentStats(): Promise<any> {
    try {
      console.log('📊 Chargement des statistiques de documents...');
      const response = await this.get<any>(`${API_ENDPOINTS.DOCUMENTS}stats/`);
      console.log('✅ Statistiques reçues:', response);
      return response;
    } catch (error) {
      console.error('❌ Erreur lors du chargement des stats:', error);
      // Retourner des stats par défaut en cas d'erreur
      return {
        total: 0,
        recent: 0,
        byType: { pdf: 0, doc: 0, xls: 0, xlsx: 0 }
      };
    }
  }
}

// Instance singleton
export const documentService = new DocumentService();
